import React from 'react'

export interface FormFieldProps {
  label: string
  name: string
  error?: string
  helperText?: string
  required?: boolean
  /**
   * MAF 26-05 (trial, display-only): when set (0..1) and there is no `error`,
   * renders an `Auto-filled · N% confidence` line under the input using the
   * helper-text styling. Defaults off, so existing call-sites are unaffected.
   */
  confidence?: number
  /** MAF 29-05: dims the label to match a locked (disabled) input. */
  disabled?: boolean
  /** Overrides the label colour when set (and not disabled). */
  labelColor?: string
  children: React.ReactNode
}

export function FormField({ label, name, error, helperText, required, confidence, disabled = false, labelColor, children }: FormFieldProps) {
  return (
    <div className="flex flex-col w-full">
      {/* Label */}
      <label
        htmlFor={name}
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-body-sm)',
          fontWeight: 'var(--weight-semibold)',
          color: disabled
            ? 'var(--color-text-disabled)'
            : labelColor ?? 'var(--color-text-primary)',
          marginBottom: '6px',
          display: 'block',
          lineHeight: 'var(--leading-normal)',
        }}
      >
        {label}
        {required && (
          <span aria-hidden="true" style={{ color: 'var(--color-action-primary)', marginLeft: '3px' }}>*</span>
        )}
      </label>

      {/* Input slot */}
      {children}

      {/* Error, helper text, and/or confidence line.
          Error takes precedence; confidence (MAF 26-05) renders only when there
          is no error and can sit alongside helper text. */}
      {error ? (
        <p
          id={`${name}-error`}
          role="alert"
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-label-sm)',
            color: 'var(--color-error-text)',
            marginTop: 'var(--spacing-1)',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          {error}
        </p>
      ) : (
        <>
          {helperText && (
            <p
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-label-sm)',
                color: 'var(--color-text-secondary)',
                marginTop: 'var(--spacing-1)',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              {helperText}
            </p>
          )}
          {confidence != null && (
            <p
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-label-sm)',
                color: 'var(--color-text-secondary)',
                marginTop: 'var(--spacing-1)',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              Auto-filled · {Math.round(confidence * 100)}% confidence
            </p>
          )}
        </>
      )}
    </div>
  )
}
